// Which refund policy the product page offers a self-service refund under.
//
// The listing's policy lives on the product doc (details.refundPolicy), which
// the seller can edit at any time. Fulfilment records the policy the buyer was
// shown on each purchase line (users/{uid}/purchases/{intentId}, items[]), and
// submitDispute judges a refund request against that record, falling back to
// the live listing only for purchases made before lines carried one. The page
// used to read the live listing alone, so a seller who switched to "No
// refunds" hid the button from every earlier buyer, although the server would
// still have taken their request.
//
// MIRROR of purchasedRefundPolicy in functions/src/lib/refund-policy.ts (two
// builds; functions code cannot be imported into src). The two are run against
// the same cases by src/lib/purchased-refund-policy.test.ts, so a change to
// either side's choice fails the suite rather than putting the button and the
// server at odds again.
//
// Pure: no React, no Firestore. The page passes in the purchase doc it read.

export type RefundPolicyShape = { enabled?: boolean; type?: string; customDays?: unknown };

/** The part of a product doc this reads: the live listing's policy. */
export interface LiveRefundPolicySource {
  details?: { refundPolicy?: RefundPolicyShape | null } | null;
}

/**
 * The refund policy a buyer's request is judged against: the one recorded on
 * their purchase line for this product, else the live listing's.
 *
 * @param purchaseData The purchase doc the buyer's entitlement points at, or
 *   undefined when it is unknown or unreadable (a guest, or not loaded yet).
 * @param productId The product whose line to read.
 * @param liveProduct The product as it is now.
 * @returns The policy to decide on, or null when neither carries one.
 */
export function purchasedRefundPolicy(
  purchaseData: Record<string, unknown> | null | undefined,
  productId: string,
  liveProduct: LiveRefundPolicySource | null | undefined,
): RefundPolicyShape | null {
  const items = Array.isArray(purchaseData?.items) ? purchaseData?.items as unknown[] : [];
  for (const raw of items) {
    const line = raw as { productId?: unknown; refundPolicy?: unknown } | null;
    if (line && line.productId === productId && line.refundPolicy && typeof line.refundPolicy === 'object') {
      return line.refundPolicy as RefundPolicyShape;
    }
  }
  return liveProduct?.details?.refundPolicy || null;
}
